package com.parmartechnologies.commerce.feature.address.viewModel

import com.parmartechnologies.business.feature.business.handler.BusinessHandler
import com.parmartechnologies.core.app.AppConfig
import com.parmartechnologies.core.feature.address.model.Address
import com.parmartechnologies.core.feature.address.model.LocationCoordinate
import com.parmartechnologies.core.feature.address.network.CreateAddressRequest
import com.parmartechnologies.core.feature.address.network.FindZipCodeRequest
import com.parmartechnologies.core.feature.address.network.PinCode
import com.parmartechnologies.core.feature.address.network.RetrieveAddressRequest
import com.parmartechnologies.core.feature.address.network.UpdateAddressRequest
import com.parmartechnologies.commerce.feature.address.handler.AddressHandler
import com.parmartechnologies.commerce.feature.address.network.AddressNetwork
import com.parmartechnologies.commerce.feature.address.repository.AddressRepository
import com.parmartechnologies.commerce.feature.alert.handler.AlertHandler
import com.parmartechnologies.core.app.AppViewModel
import com.parmartechnologies.communication.core.handler.AuthHandler
import com.parmartechnologies.storage.constant.KeyConstant
import com.parmartechnologies.storage.store.UserDefaults
import com.parmartechnologies.storage.table.address.AddressDatabaseTable
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import kotlin.time.ExperimentalTime

class AddressViewModel : AppViewModel() {
    private val repository: AddressRepository = AddressHandler.shared().repository
    val allAddress: StateFlow<List<Address>>
        get() = repository.allAddress

    val selectedAddress: StateFlow<Address>
        get() = repository.selectedAddress

    val primaryAddress: StateFlow<Address>
        get() = repository.primaryAddress

    val primaryAddressEditing: StateFlow<Address>
        get() = repository.primaryAddressEditing

    val newAddress: StateFlow<Address> get() = repository.newAddress
    val editAddress: StateFlow<Address> get() = repository.editAddress
    val pickedLocation: StateFlow<LocationCoordinate?> get() = repository.pickedLocation

    fun setNewAddress(address: Address) {
        scope.launch {
            repository.newAddressState.emit(address)
        }
    }

    fun setEditAddress(address: Address) {
        scope.launch {
            repository.editAddressState.emit(address)
        }
    }

    fun setPickedLocation(locationCoordinate: LocationCoordinate?) {
        scope.launch {
            val existingAddress = newAddress
            existingAddress.value.Location = LocationCoordinate()
            repository.newAddressState.emit(existingAddress.value)
            repository.pickedLocationState.emit(
                locationCoordinate
            )
        }
    }

    fun setHouseNumberForNewAddress(value: String) {
        scope.launch {
            val existingAddress = newAddress.value
            existingAddress.House = value
            repository.newAddressState.emit(existingAddress)
        }
    }

    fun setAreaForNewAddress(value: String) {
        scope.launch {
            val existingAddress = newAddress.value
            existingAddress.Area = value
            repository.newAddressState.emit(existingAddress)
        }
    }

    fun setLandmarkForNewAddress(value: String) {
        scope.launch {
            val existingAddress = newAddress.value
            existingAddress.LandMark = value
            repository.newAddressState.emit(existingAddress)
        }
    }

    fun setPinCodeForNewAddress(value: String) {
        scope.launch {
            val existingAddress = newAddress.value
            try {
                existingAddress.ZipCode = value.toLong()
            }catch (e:Exception){

            }

            repository.newAddressState.emit(existingAddress)
        }
    }

    fun setCityForNewAddress(value: String) {
        scope.launch {
            val existingAddress = newAddress.value
            existingAddress.City = value
            repository.newAddressState.emit(existingAddress)
        }
    }

    fun setStateForNewAddress(value: String) {
        scope.launch {
            val existingAddress = newAddress.value
            existingAddress.State = value
            repository.newAddressState.emit(existingAddress)
        }
    }

    fun setPrimaryAddressForCustomer() {
        var isPrimaryAddressSet = false
        allAddress.value.forEach {
            if (it.IsPrimary == 1) {
                scope.launch {
                    repository.primaryAddressState.emit(it)
                    isPrimaryAddressSet = true
                }
            }
        }
        if (!isPrimaryAddressSet && allAddress.value.isNotEmpty()) {
            scope.launch {
                repository.editAddressState.emit(allAddress.value.first())
            }
        }
    }

    fun loadPrimaryAddress(callback: (Address?) -> Unit) {
        val addressString = UserDefaults.shared().retrieve(KeyConstant.primaryAddress)
        if (addressString.isNotEmpty()) {
            val address = jsonDecoder.decodeFromString<Address>(addressString)
            scope.launch {
                repository.primaryAddressState.emit(address)
            }
            callback(address)
        } else {
            callback(null)
        }
    }

    fun setPrimaryAddress(value: Address?, callback: () -> Unit = {}) {
        if (value != null) {
            UserDefaults.shared().store(KeyConstant.primaryAddress, jsonDecoder.encodeToString(value))
            scope.launch {
                repository.primaryAddressState.emit(value)
                delay(50)
                callback()
            }
        }
    }

    fun setPrimaryAddressZipCode(value: String) {
        if (value.isNotEmpty()) {
            scope.launch {
                try {
                    val existingAddress = primaryAddressEditing.value
                    existingAddress.ZipCode = value.toLong()
                    repository.primaryAddressEditingState.emit(existingAddress)
                } catch (e: Exception) {

                }
            }
        }
    }

    @OptIn(ExperimentalTime::class)
    fun create(callback: (Boolean) -> Unit = {}) {
        scope.launch {
            val request = CreateAddressRequest(
                Name = newAddress.value.Name,
                FeatureObjectID = AuthHandler.shared().UserID,
                UserID = AuthHandler.shared().UserID,
                House = newAddress.value.House,
                Area = newAddress.value.Area,
                LandMark = newAddress.value.LandMark,
                ZipCode = newAddress.value.ZipCode.toLong(),
                City = newAddress.value.City,
                State = newAddress.value.State,
                Country = newAddress.value.Country,
                Location = newAddress.value.Location,
            )
            startLoading()
            AddressNetwork().create(request) {
                stopLoading()

                if (it?.payload?._id.isNullOrEmpty()) {
                    callback(true)
                    AlertHandler.shared()
                        .showAlert("Oops!", "Something went wrong, please try after some time")
                } else {
                    callback(false)
                    scope.launch { repository.newAddressState.emit(Address()) }
//                    AppNavigator.shared().navigator?.pop()
                    AlertHandler.shared().showAlert("Congrats!", "Address created successfully")
                }
            }
        }
    }


    @OptIn(ExperimentalTime::class)
    fun createBusinessAddress() {
        scope.launch {
            val request = CreateAddressRequest(
                BusinessID = BusinessHandler.shared().myBusinessId,
                UserID = AuthHandler.shared().UserID,
                Name = newAddress.value.Name,
                FeatureObjectID = AuthHandler.shared().UserID,
                House = newAddress.value.House,
                Area = newAddress.value.Area,
                LandMark = newAddress.value.LandMark,
                ZipCode = newAddress.value.ZipCode.toLong(),
                City = newAddress.value.City,
                State = newAddress.value.State,
                Country = newAddress.value.Country,
                Location = newAddress.value.Location,
            )
            startLoading()
            AddressNetwork().create(request) {
                stopLoading()
                if (it?.payload?._id.isNullOrEmpty()) {
                    AlertHandler.shared()
                        .showAlert("Oops!", "Something went wrong, please try after some time")
                } else {
                    scope.launch { repository.newAddressState.emit(Address()) }
//                    AppNavigator.shared().navigator?.pop()
                    AlertHandler.shared().showAlert("Congrats!", "Address created successfully")
                }
            }
        }
    }

    fun retrieve(callback: () -> Unit = {}) {
        loadAllAddressForUser()
        scope.launch {
            val request = RetrieveAddressRequest(
                BusinessID = "",
                featureObjectID = AuthHandler.shared().UserID,
            )
            startLoading()
            AddressNetwork().retrieve(request) {
                stopLoading()
                scope.launch {
                    AddressDatabaseTable().insert(it?.payload)
                    callback()
                }
                loadAllAddressForUser()
            }
        }
    }

    fun findZipCode(value: String, callback: (List<PinCode>) -> Unit = {}) {
        loadAllAddressForUser()
        scope.launch {
            val request = FindZipCodeRequest(
                country = AppConfig.shared().countryCode,
                pinCode = value
            )
            AddressNetwork().findPinCode(request) { response ->
                scope.launch {
                    if (response?.payload.isNullOrEmpty() || response?.payload?.isEmpty() == true) {
                        callback(arrayListOf())
                    } else {
                        callback(response!!.payload)
                        if (response.payload.isNotEmpty()) {
                            allAddress
                        }
                    }
                }
            }
        }
    }

    fun loadAllAddressForUser() {
        scope.launch {
            AddressDatabaseTable().retrieveForUser(AuthHandler.shared().UserID) { data ->
                scope.launch {
                    repository.allAddressState.emit(data)
                }
            }
        }
    }


    fun setHouseNumberEditAddress(value: String) {
        scope.launch {
            val existingAddress = editAddress.value
            existingAddress.House = value
            repository.editAddressState.emit(existingAddress)
        }
    }

    fun setAreaForEditAddress(value: String) {
        scope.launch {
            val existingAddress = editAddress.value
            existingAddress.Area = value
            repository.editAddressState.emit(existingAddress)
        }
    }

    fun setLandmarkForEditAddress(value: String) {
        scope.launch {
            val existingAddress = editAddress.value
            existingAddress.LandMark = value
            repository.editAddressState.emit(existingAddress)
        }
    }

    fun setPinCodeForEditAddress(value: String) {
        scope.launch {
            val existingAddress = editAddress.value
            existingAddress.ZipCode = value.toLong()
            repository.editAddressState.emit(existingAddress)
        }
    }

    fun setCityForEditAddress(value: String) {
        scope.launch {
            val existingAddress = editAddress.value
            existingAddress.City = value
            repository.editAddressState.emit(existingAddress)
        }
    }

    fun setStateForEditAddress(value: String) {
        scope.launch {
            val existingAddress = editAddress.value
            existingAddress.State = value
            repository.editAddressState.emit(existingAddress)
        }
    }

    fun setStatePrimaryEditAddress(value: Boolean) {
        scope.launch {
            val existingAddress = editAddress.value
            if(value){
                existingAddress.IsPrimary = 1
            }else{
                existingAddress.IsPrimary = 0
            }
            repository.editAddressState.emit(existingAddress)
        }
    }

    @OptIn(ExperimentalTime::class)
    fun update() {
        scope.launch {
            val request = UpdateAddressRequest(
                id = editAddress.value._id,
                uniqueId = editAddress.value.UniqueId,
                UserID = AuthHandler.shared().UserID,
                IsPrimary = editAddress.value.IsPrimary,
                name = newAddress.value.Name,
                featureObjectID = AuthHandler.shared().UserID,
                House = newAddress.value.House,
                Area = newAddress.value.Area,
                LandMark = newAddress.value.LandMark,
                ZipCode = newAddress.value.ZipCode.toLong(),
                City = newAddress.value.City,
                State = newAddress.value.State,
                Country = newAddress.value.Country,
                Location = newAddress.value.Location,
            )
            startLoading()
            AddressNetwork().update(request) {
                stopLoading()
                if (it?.status != "Success") {
                    AlertHandler.shared()
                        .showAlert("Oops!", "Something went wrong, please try after some time")
                } else {
                    scope.launch { repository.newAddressState.emit(Address()) }
//                    AppNavigator.shared().navigator?.pop()
                    AlertHandler.shared()
                        .showSuccessAlert("Done!", "Address updated successfully")
                }
            }
        }
    }
}
